/**
 * Authentication Manager
 * 
 * Routes authentication based on device type:
 * - Mobile devices (tablets/phones) -> Amazon Federate device flow
 * - Desktop devices -> Existing Cognito authentication
 * 
 * Maintains backward compatibility with existing Cognito workflows.
 */

import { 
  AuthManager as IAuthManager,
  AuthResult,
  TokenSet,
  UserContext,
  AuthenticationType,
  DeviceType
} from '../types/deviceFlowTypes';
import { deviceDetectionService } from './deviceDetectionService';
import { deviceFlowHandler } from './deviceFlowHandler';
import authService from './authService';
import { tokenManager } from './tokenManager';
import { getPayloadValue as getVal, extractLdapGroups } from '../utils/jwtPayloadMapper';

export class AuthManager implements IAuthManager {
  private currentAuthType: AuthenticationType | null = null;
  private deviceType: DeviceType;

  constructor() {
    // Detect device type on initialization
    this.deviceType = deviceDetectionService.getDeviceType();
    
    // Determine current auth type from stored tokens
    this.currentAuthType = tokenManager.getStoredAuthType();
    
    console.log(`AuthManager initialized - Device: ${this.deviceType}, Auth Type: ${this.currentAuthType}`);
  }

  /**
   * Initialize authentication system
   * Sets up interceptors and validates existing authentication state
   * Enhanced to properly restore federate tokens from localStorage on PWA restart
   */
  async initializeAuth(): Promise<void> {
    try {
      console.log('[AuthManager] Starting authentication initialization');
      console.log('[AuthManager] Device type:', this.deviceType);
      console.log('[AuthManager] Current auth type:', this.currentAuthType);

      if (this.deviceType === 'mobile') {
        await this.initializeMobileAuth();
      } else {
        await this.initializeDesktopAuth();
      }

      console.log('[AuthManager] Authentication system initialized successfully');
      console.log('[AuthManager] Final auth type:', this.currentAuthType);
    } catch (error) {
      console.error('[AuthManager] Failed to initialize authentication system:', error);
      throw new Error(`Authentication initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Initialize mobile device authentication (federate)
   * Restores tokens from localStorage and handles refresh if needed
   */
  private async initializeMobileAuth(): Promise<void> {
    console.log('[AuthManager] Initializing mobile authentication');
    
    const tokens = await this.restoreTokensFromStorage();
    if (!tokens) {
      console.log('[AuthManager] No federate tokens found in localStorage');
      this.currentAuthType = null;
      return;
    }

    await this.handleAccessTokenState(tokens);
    await this.validateDeviceFlowAuthenticationState();
  }

  /**
   * Initialize desktop device authentication (Cognito)
   */
  private async initializeDesktopAuth(): Promise<void> {
    console.log('[AuthManager] Initializing desktop Cognito authentication');
    
    const isAuthenticated = this.isAuthenticated();
    if (isAuthenticated) {
      console.log('[AuthManager] Existing Cognito authentication found');
    } else {
      console.log('[AuthManager] No Cognito authentication found');
    }
  }

  /**
   * Restore tokens from localStorage
   * Returns null if no tokens found
   */
  private async restoreTokensFromStorage(): Promise<TokenSet | null> {
    const tokens = tokenManager.getTokens('federate');
    
    if (tokens) {
      console.log('[AuthManager] Found federate tokens in localStorage:', {
        hasAccessToken: !!tokens.accessToken,
        hasIdToken: !!tokens.idToken,
        hasRefreshToken: !!tokens.refreshToken,
        expiresAt: new Date(tokens.expiresAt).toLocaleString()
      });
      
      this.logTokenStatus(tokens);
    }
    
    return tokens;
  }

  /**
   * Log token expiration status for debugging
   */
  private logTokenStatus(tokens: TokenSet): void {
    const isExpired = tokenManager.isTokenExpired(tokens.accessToken);
    const shouldRefresh = tokenManager.shouldRefreshToken(tokens.accessToken);
    const timeRemaining = Math.round((tokens.expiresAt - Date.now()) / 1000 / 60);
    
    console.log('[AuthManager] Token status:', {
      isExpired,
      shouldRefresh,
      timeRemaining: `${timeRemaining} minutes`
    });
  }

  /**
   * Handle token state based on expiration status
   * Automatically refreshes access token if expired or expiring soon
   */
  private async handleAccessTokenState(tokens: TokenSet): Promise<void> {
    const isAccessTokenExpired = tokenManager.isTokenExpired(tokens.accessToken);
    const shouldRefreshAccessToken = tokenManager.shouldRefreshToken(tokens.accessToken);
    
    // Refresh if access token is expired OR expiring soon (within 1 minute)
    if ((isAccessTokenExpired || shouldRefreshAccessToken) && tokens.refreshToken) {
      await this.refreshAccessToken(isAccessTokenExpired);
    } else if (!isAccessTokenExpired) {
      console.log('[AuthManager] Access token is still valid, no refresh needed');
      this.currentAuthType = 'federate';
    }
  }

  /**
   * Refresh access token using refresh token
   * @param clearStateOnFailure - If true, clears auth state when refresh fails (for expired access tokens)
   *                               If false, keeps auth state when refresh fails (for access tokens expiring soon)
   */
  private async refreshAccessToken(clearStateOnFailure: boolean): Promise<void> {
    const status = clearStateOnFailure ? 'expired' : 'expiring soon';
    console.log(`[AuthManager] Access token ${status}, refreshing using refresh token...`);
    
    try {
      await deviceFlowHandler.refreshTokens();
      console.log('[AuthManager] Access token refresh successful');
      this.currentAuthType = 'federate';
    } catch (refreshError) {
      if (clearStateOnFailure) {
        console.error('[AuthManager] Access token refresh failed:', refreshError);
        console.log('[AuthManager] Clearing invalid authentication state');
        await deviceFlowHandler.clearInvalidAuthenticationState();
        this.currentAuthType = null;
      } else {
        console.warn('[AuthManager] Access token refresh failed, will retry on next API call:', refreshError);
        // Keep auth state - access token is still valid for now
        this.currentAuthType = 'federate';
      }
    }
  }

  /**
   * Validate authentication state using deviceFlowHandler
   * Clears invalid state if validation fails
   */
  private async validateDeviceFlowAuthenticationState(): Promise<void> {
    if (this.currentAuthType !== 'federate') {
      return;
    }

    const validationResult = await deviceFlowHandler.validateAuthenticationState();
    
    if (!validationResult.isValid) {
      console.log('[AuthManager] DeviceFlowHandler validation failed:', validationResult.reason);
      
      if (validationResult.shouldClear) {
        console.log('[AuthManager] Clearing invalid federate authentication state');
        await deviceFlowHandler.clearInvalidAuthenticationState();
        this.currentAuthType = null;
      }
    } else {
      console.log('[AuthManager] DeviceFlowHandler validation successful');
    }
  }

  /**
   * Authenticate user based on device type
   * Routes to appropriate authentication flow
   */
  async authenticateUser(): Promise<AuthResult> {
    try {
      console.log(`Authenticating user - Device: ${this.deviceType}`);

      // Route authentication based on device type (Requirements 1.1, 2.1)
      if (this.deviceType === 'mobile') {
        return await this.authenticateWithDeviceFlow();
      } else {
        return await this.authenticateWithCognito();
      }
    } catch (error) {
      console.error('Authentication failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      };
    }
  }

  /**
   * Authenticate using Amazon Federate device flow (mobile devices)
   */
  private async authenticateWithDeviceFlow(): Promise<AuthResult> {
    try {
      console.log('Initiating device flow authentication for mobile device');

      // Check if we already have valid federate tokens
      const existingTokens = tokenManager.getTokens('federate');
      if (existingTokens && !tokenManager.isTokenExpired(existingTokens.accessToken)) {
        console.log('Valid federate tokens found, using existing authentication');
        
        const userInfo = this.extractUserInfoFromToken(existingTokens.idToken);
        this.currentAuthType = 'federate';
        
        return {
          success: true,
          tokens: existingTokens,
          userInfo
        };
      }

      // Initiate device flow authentication
      await deviceFlowHandler.initiateDeviceFlow({
        state: window.location.href,
        clientRequestId: this.generateClientRequestId()
      });

      // The device flow handler will redirect to Amazon Federate
      // This method will not return normally - the page will redirect
      // The callback will be handled by handleCallback method
      
      return {
        success: true,
        tokens: undefined, // Will be set after callback
        userInfo: undefined // Will be set after callback
      };

    } catch (error) {
      console.error('Device flow authentication failed:', error);
      return {
        success: false,
        error: `Device flow authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Authenticate using existing Cognito flow (desktop devices)
   * Maintains backward compatibility (Requirement 2.3)
   */
  private async authenticateWithCognito(): Promise<AuthResult> {
    try {
      console.log('Using existing Cognito authentication for desktop device');

      // Use existing authService for Cognito authentication
      const cognitoUser = await authService.login();
      
      if (!cognitoUser.token) {
        // authService.login() will handle the federated sign-in redirect
        // This is the existing behavior that we want to preserve
        return {
          success: true,
          tokens: undefined, // Will be set after Cognito callback
          userInfo: undefined // Will be set after Cognito callback
        };
      }

      // Convert Cognito user to our standard format
      const tokens: TokenSet = {
        accessToken: cognitoUser.token,
        idToken: cognitoUser.token, // Cognito uses same token for both
        refreshToken: '', // Cognito refresh is handled by AWS Amplify
        expiresAt: this.extractTokenExpiry(cognitoUser.token)
      };

      const userInfo = {
        userAlias: cognitoUser.alias,
        firstName: cognitoUser.firstName,
        lastName: cognitoUser.lastName,
        email: cognitoUser.email,
        ldapGroups: cognitoUser.ldap
      };

      // Store tokens using existing Cognito keys (maintains compatibility)
      tokenManager.storeTokens(tokens, 'cognito');
      this.currentAuthType = 'cognito';

      return {
        success: true,
        tokens,
        userInfo
      };

    } catch (error) {
      console.error('Cognito authentication failed:', error);
      return {
        success: false,
        error: `Cognito authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Handle authentication callback (for device flow)
   * This method is called after redirect from Amazon Federate
   */
  async handleAuthenticationCallback(): Promise<AuthResult> {
    try {
      console.log('Handling authentication callback');

      // Only handle device flow callbacks for mobile devices
      if (this.deviceType !== 'mobile') {
        throw new Error('Authentication callback only supported for mobile devices');
      }

      // Handle the device flow callback
      const tokenResponse = await deviceFlowHandler.handleCallback();
      
      // Convert to our standard format
      const tokens: TokenSet = {
        accessToken: tokenResponse.accessToken,
        idToken: tokenResponse.idToken,
        refreshToken: tokenResponse.refreshToken,
        expiresAt: this.extractTokenExpiry(tokenResponse.accessToken)
      };

      // Store tokens with federate prefixes (Requirement 1.4)
      tokenManager.storeTokens(tokens, 'federate');
      this.currentAuthType = 'federate';

      return {
        success: true,
        tokens,
        userInfo: tokenResponse.userInfo
      };

    } catch (error) {
      console.error('Failed to handle authentication callback:', error);
      return {
        success: false,
        error: `Callback handling failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Refresh authentication tokens
   * Routes to appropriate refresh method based on auth type
   */
  async refreshTokens(): Promise<TokenSet> {
    try {
      if (!this.currentAuthType) {
        throw new Error('No active authentication to refresh');
      }

      console.log(`Refreshing ${this.currentAuthType} tokens`);

      if (this.currentAuthType === 'federate') {
        // Use device flow handler to refresh tokens
        const refreshedTokens = await deviceFlowHandler.refreshTokens();
        
        const tokens: TokenSet = {
          accessToken: refreshedTokens.accessToken,
          idToken: refreshedTokens.idToken,
          refreshToken: refreshedTokens.refreshToken,
          expiresAt: refreshedTokens.expiresAt
        };

        tokenManager.storeTokens(tokens, 'federate');
        return tokens;

      } else {
        // For Cognito, use the token manager's refresh logic
        return await tokenManager.refreshAccessToken('cognito');
      }

    } catch (error) {
      console.error('Token refresh failed:', error);
      throw new Error(`Token refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Logout user and clear authentication state
   */
  async logout(): Promise<void> {
    try {
      console.log(`Logging out ${this.currentAuthType} user`);

      if (this.currentAuthType === 'federate') {
        // Clear federate tokens and state
        await deviceFlowHandler.clearInvalidAuthenticationState();
        tokenManager.clearTokens('federate');
      } else if (this.currentAuthType === 'cognito') {
        // Use existing Cognito logout
        authService.logout();
        tokenManager.clearTokens('cognito');
      }

      this.currentAuthType = null;
      console.log('User logged out successfully');

    } catch (error) {
      console.error('Logout failed:', error);
      throw new Error(`Logout failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    if (!this.currentAuthType) {
      return false;
    }

    const tokens = tokenManager.getTokens(this.currentAuthType);
    if (!tokens) {
      return false;
    }

    // Check if access token is expired
    return !tokenManager.isTokenExpired(tokens.accessToken);
  }

  /**
   * Get current authentication type
   */
  getAuthType(): AuthenticationType | null {
    return this.currentAuthType;
  }

  /**
   * Get current device type
   */
  getDeviceType(): DeviceType {
    return this.deviceType;
  }

  /**
   * Get current user context
   */
  getCurrentUserContext(): UserContext | null {
    if (!this.isAuthenticated() || !this.currentAuthType) {
      return null;
    }

    const tokens = tokenManager.getTokens(this.currentAuthType);
    if (!tokens) {
      return null;
    }

    try {
      const userInfo = this.extractUserInfoFromToken(tokens.idToken);
      
      return {
        userAlias: userInfo.userAlias || 'unknown',
        isAdmin: this.checkAdminStatus(userInfo.ldapGroups || []),
        authType: this.currentAuthType,
        tokenExpiry: tokens.expiresAt,
        ldapGroups: userInfo.ldapGroups,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        siteLocation: userInfo.siteLocation || 'unknown'
      };

    } catch (error) {
      console.error('Failed to extract user context:', error);
      return null;
    }
  }

  /**
   * Extract user information from JWT token.
   */
  private extractUserInfoFromToken(token: string): any {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const email = getVal(payload, "email");

      return {
        userAlias:
          (payload?.identities as any)?.[0]?.userId ||
          getVal(payload, "preferred_username") ||
          email?.split("@")[0] ||
          getVal(payload, "uid") ||
          getVal(payload, "sub") ||
          "unknown",
        email,
        firstName: getVal(payload, "given_name"),
        lastName: getVal(payload, "family_name"),
        siteLocation: getVal(payload, "LOCATION") || getVal(payload, "custom:location"),
        ldapGroups: extractLdapGroups(payload),
      };
    } catch (error) {
      console.error('Failed to extract user info from token:', error);
      return {};
    }
  }


  /**
   * Extract token expiry from JWT token
   */
  private extractTokenExpiry(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : Date.now() + (15 * 60 * 1000); // Default 15 minutes
    } catch (error) {
      console.error('Failed to extract token expiry:', error);
      return Date.now() + (15 * 60 * 1000); // Default 15 minutes
    }
  }

  /**
   * Check if user has admin privileges based on LDAP groups
   */
  private checkAdminStatus(ldapGroups: string[]): boolean {
    // Define admin groups - this should be configurable
    const adminGroups = ['admin', 'administrators', 'whs-eap-admin'];
    return ldapGroups.some(group => 
      adminGroups.includes(group.toLowerCase())
    );
  }

  /**
   * Generate a unique client request ID for tracking
   */
  private generateClientRequestId(): string {
    return `auth-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}

// Export singleton instance
export const authManager = new AuthManager();
export default authManager;